<template>
	<default-layout v-if="!errorCode">
		<spinner v-if="!showSignInOptions" />
		<template v-if="showSignInOptions && invitationId">
			<b-card class="mx-auto" header-tag="header" align="center" style="max-width: 27rem; display: inline-block;">
				<h6>{{ $t(`${appNameCapitalised} uses HP Accounts to sign in`) }}</h6>
				<div class="mt-3">
					<label v-t="'I have an HP Account'" />
					<b-button block variant="primary" @click="gotoLoginPage">{{ $t('SIGN IN') }} </b-button>
				</div>
				<div class="mt-3">
					<label> {{ $t("I don't have an HP Account") }}</label>
					<b-button block variant="outline-primary" @click="gotoHPSignUp">{{ $t('CREATE HP ACCOUNT') }}</b-button>
				</div>
			</b-card>
			<Footer />
		</template>
	</default-layout>
	<error-card
		v-else
		:code="errorCode"
		:message="errorMessage"
		:app-info="appInfo"
		:title="errorCode === 1006 ? 'Registration failed' : 'Sign in failed'"
	>
		<template v-if="appInfo.app_name === 'device-agent'" #logo>
			<img src="/images/hp-device-agent-logo.svg" alt="HP" />
		</template>
		<template v-else-if="appInfo.app_name === 'brand-centre'" #logo>
			<img src="/images/hp-brand-centre-logo.svg" alt="HP" />
		</template>
		<template v-else-if="appInfo.app_name === 'sfl'" #logo>
			<img src="/images/hp-sfl-logo.svg" alt="HP" />
		</template>
		<template v-else-if="appInfo.app_name === 'sf'" #logo>
			<img src="/images/hp-sf-logo.svg" alt="HP" />
		</template>
	</error-card>
</template>

<script type="text/javascript">
import _get from 'lodash.get';
import axios from 'axios';
import Spinner from '@/components/Spinner';
import ErrorCard from '@/components/ErrorCard';
import DefaultLayout from '@/components/DefaultLayout';
import Footer from '@/components/Footer';

export default {
	name: 'InvitationPage',
	components: {
		Spinner,
		ErrorCard,
		DefaultLayout,
		Footer
	},
	data() {
		const {
			authBase,
			ssoRedirectUrl = `${window.location.origin}/invitation/completed`,
			apps = {},
			app
		} = window.$config;

		return {
			showSignInOptions: false,
			authBase,
			ssoRedirectUrl,
			loginPageBase: '',
			appNameCapitalised: '',
			email: '',
			errorMessage: '',
			errorCode: 0,
			apps,
			app
		};
	},
	computed: {
		invitationId() {
			const { id } = this.$route.params || {};
			return id;
		},
		appInfo() {
			const { app_name, state } = this.$route.query || {};
			let stateFromUrl = {};
			try {
				stateFromUrl = JSON.parse(atob(state));
			} catch (e) {
				stateFromUrl = {};
			}
			const appInfo = { app_name, ...stateFromUrl };
			Object.assign(appInfo, this.apps[appInfo.app_name], this.app);
			return appInfo;
		},
		hpEndpoint() {
			return `${this.loginPageBase}/authorize?response_type=code&client_id=${this.appInfo.clientId}&redirect_uri=${this.ssoRedirectUrl}&scope=email+openid+user.profile.read`;
		}
	},
	async created() {
		const { code: hpCode, state } = this.$route.query || {};
		await this.getClientConfig();
		if (this.errorCode) return;
		this.appNameCapitalised = this.appInfo.appName || 'The application';
		if (this.invitationId && !hpCode) {
			try {
				// get invitation
				const { valid, email } = await this.getInvitation(this.invitationId);
				if (valid) {
					this.showSignInOptions = true;
					this.email = email;
				} else {
					this.errorMessage = 'Invitation already used or expired';
					this.errorCode = 1006;
				}
			} catch (err) {
				const { code, message } = _get(err, 'response.data', {});
				if (code === 400 && (message === 'Invitation Invalid' || message === 'Invitation already used or expired')) {
					this.errorMessage = 'Invitation already used or expired';
					this.errorCode = 1006;
				}
				this.errorCode = 1000;
			}
		} else if (hpCode && this.appInfo.invitationId) {
			try {
				const { token } = await this.hpInvitationIdLogin(hpCode);
				await this.gotoApp(token, state);
			} catch (err) {
				const { code, message } = _get(err, 'response.data', {});

				if (code === 400 && message === 'The provided authorization code is expired or invalid') {
					this.gotoLoginPage();
					return this.$router.replace({ query: {} });
				} else if (message === 'Invitation Invalid' || message === 'Invitation already used or expired') {
					this.errorMessage = 'Invitation already used or expired';
					this.errorCode = 1006;
					return;
				} else if (message === 'HP employees should login with ONEHP and not local accounts') {
					this.errorMessage = message;
					this.errorCode = 1007;
				}
				this.errorMessage = message;
				this.errorCode = 1005;
			}
		} else {
			this.errorCode = 1001;
		}
	},
	methods: {
		async hpInvitationIdLogin(hpCode) {
			const url = `${this.authBase}/sso/invitation/completed/${this.appInfo.invitationId}`;
			const payload = {
				clientName: this.appInfo.app_name,
				code: hpCode,
				redirectUrl: this.ssoRedirectUrl
			};
			const {
				data: { token }
			} = await axios.post(url, payload);
			return { token };
		},
		async getInvitation(id) {
			const { data } = await axios.get(`${this.authBase}/invitation/${id}/valid`);
			return data;
		},
		async getClientConfig() {
			try {
				if (!this.appInfo.app_name || !this.appInfo.appName) throw new Error();
				const { data: clientConfig } = await axios.get(`${this.authBase}/sso/client/${this.appInfo.app_name}`);
				if (!clientConfig || !clientConfig.clientId || !clientConfig.redirectUrl) throw new Error();

				this.loginPageBase = clientConfig.loginPageUrl;
				this.appInfo.clientId = clientConfig.clientId;
				this.appInfo.sso = clientConfig.sso;
				if (this.appInfo.sso !== 'hpid') throw new Error(); // HPID is the only supported SSO for now for invitations

				// Set the redirectUrl based on the client config. The url of the device agent needs to have included the port
				this.appInfo.redirectUrl =
					location.hostname === 'localhost' ? 'http://localhost:8080/#/' : clientConfig.redirectUrl;
				if (this.appInfo.app_name === 'device-agent') {
					this.appInfo.redirectUrl = `${clientConfig.redirectUrl}:${this.appInfo.port}/#/`;
				}
			} catch (err) {
				this.errorCode = 1001;
			}
		},
		gotoLoginPage() {
			const state = {
				app_name: this.appInfo.app_name,
				invitationId: this.invitationId
			};
			const encodedState = btoa(JSON.stringify(state));
			window.location.href = `${this.hpEndpoint}&state=${encodedState}&login_hint=${this.email}`;
		},
		gotoHPSignUp() {
			const state = {
				app_name: this.appInfo.app_name,
				invitationId: this.invitationId
			};
			const encodedState = btoa(JSON.stringify(state));
			window.location.href = `${this.hpEndpoint}&state=${encodedState}&email=${this.email}&target=create`;
		},
		async gotoApp(token, state) {
			if (state) {
				try {
					window.location.href = `${this.appInfo.redirectUrl}?token=${token}`;
				} catch (e) {
					this.errorCode = 1003;
				}
			} else {
				this.errorCode = 1002;
			}
		}
	}
};
</script>

<style lang="scss">
.AuthorizePage {
	height: 100vh;
	width: 100vw;

	> .row.justify-content-center {
		height: 100%;
		> .col-sm-6.col-lg-4 {
			align-self: center;
		}
	}
}
</style>
